/**
 * reconnectPin — the person a reconnect just landed you on (signup E3b-1 step 6;
 * SYSTEM_Build-spec §4c-iv, signup-meetings § Where a connect lands).
 *
 * After a reconnect both phones land on People with that person ON TOP and the
 * `Already connected …` line in their met line's slot. It holds until you go
 * somewhere else in Royale (Chats, someone's page — Detail included — your
 * Profile) and come back; locking the phone or switching apps doesn't clear it,
 * and there's no timer. So it's stored (it must outlive a lock and a reload), per
 * account (another account on the device never sees it), and cleared by the one
 * navigation door (NavigationContext.navigateTo → clearPinOnNavigate).
 *
 * One pin at a time: a second reconnect replaces the first (Claude's call, plan
 * § Execution log). The line is always the at/in one — no share-change variant
 * (Mark, 2026-10-20).
 *
 * Permanent data layer: no React. People listens for PIN_EVENT to re-read.
 */
import { getProfileUserId } from '../../utils/identityUtils';
import { isNewExperience } from '../../utils/newExperience';

export const PIN_EVENT = 'royale:reconnect-pin';
const keyFor = (id) => `people_reconnect_pin:${id}`;

const announce = () => {
  try {
    window.dispatchEvent(new Event(PIN_EVENT));
  } catch {
    // no window (tests without a DOM) — nothing listens
  }
};

/** @returns {{room: string, at: string}|null} */
export const readReconnectPin = () => {
  const id = getProfileUserId();
  if (!id) return null;
  try {
    const pin = JSON.parse(localStorage.getItem(keyFor(id)) || 'null');
    return pin?.room ? pin : null;
  } catch {
    return null;
  }
};

/**
 * Pin a person after a reconnect (set from the host's verdict only — a reply
 * with no verdict sets none, plan step 10).
 * @param {string} room
 */
export const setReconnectPin = (room) => {
  const id = getProfileUserId();
  if (!id || !room || !isNewExperience()) return;
  localStorage.setItem(keyFor(id), JSON.stringify({ room, at: new Date().toISOString() }));
  announce();
};

export const clearReconnectPin = () => {
  const id = getProfileUserId();
  if (!id || localStorage.getItem(keyFor(id)) === null) return;
  localStorage.removeItem(keyFor(id));
  announce();
};

/**
 * Going anywhere in Royale but People clears the pin (§4c-iv). Called by the
 * navigation door on every navigateTo.
 * @param {string} screen - where the person is going
 */
export const clearPinOnNavigate = (screen) => {
  if (screen === 'new-people' || !isNewExperience()) return;
  clearReconnectPin();
};
